/**
 * Core domain vocabulary (§5.1).
 *
 * Each set is exported twice: as a frozen `as const` tuple (so `packages/protocol`
 * can build a Zod enum from it without restating the members) and as a derived
 * union type. The tuple is the single source of truth — adding a cell here makes
 * it appear in the wire schema, the seat table, and the projection switch
 * exhaustiveness checks at once.
 */
export const TEAM_IDS = ['BLUFOR', 'REDFOR'];
/** The other team. Used constantly by the detection code (§5.3). */
export function opposing(team) {
    return team === 'BLUFOR' ? 'REDFOR' : 'BLUFOR';
}
/**
 * The eight player cells of §5, in briefing order. `GM` is a seat but not a cell —
 * see `SEAT_IDS`.
 *
 * `CW` is the cyber *seat*, named like its siblings `OW` and `EW` (RB §5.6).
 * Three other things in this file are still `CYBER` and none of them moves with
 * it: the console tab, the asset segment, and `RESOLUTION_PHASES[0]`, whose index
 * seeds every roll. A find-and-replace across this file corrupts the engine.
 */
export const CELLS = ['CREWCOM', 'SDA', 'MW', 'OW', 'EW', 'CW', 'FIRES', 'ISR'];
/** Seats are the eight cells plus the umpire (§3.2). */
export const SEAT_IDS = [...CELLS, 'GM'];
/**
 * The console's five modules (M5 Q1), which are also its tab set.
 *
 * Vocabulary, not layout: the console frame is one shell with a tab strip, and a
 * seat sees the subset its scenario gives it (`ScenarioDefinitionV0.seats`). Here
 * rather than in the client because the scenario document names these, so a typo
 * has to be catchable at load.
 *
 * `SDA` is the orbital board — the design's SPACE and SDA tabs are one module —
 * and it collides with the *cell* named SDA only in spelling: OW, EW and FIRES
 * all work that board.
 */
export const CONSOLE_TABS = ['THEATRE', 'SDA', 'CYBER', 'MW', 'INTEL'];
/** Orbital bands (§3.1). Ring sizes live in `BAND_SLOT_COUNTS`. */
export const BANDS = ['LEO', 'MEO', 'GEO', 'HEO'];
/**
 * Slots per band (§3.1). HEO has 4, everything else 24. Slot arithmetic is modular
 * over these values and distance is the shorter way around the ring (§7.6 phase 4).
 */
export const BAND_SLOT_COUNTS = Object.freeze({
    LEO: 24,
    MEO: 24,
    GEO: 24,
    HEO: 4,
});
/** The detection ladder (§1.2). `HIDDEN` is represented as the absence of a record. */
export const RUNGS = ['HIDDEN', 'FOUND', 'FIXED', 'TRACKED'];
/** Asset status (§1.1). Derived from active effects, never stored (§5.2). */
export const STATUSES = ['NOMINAL', 'DEGRADED', 'OFFLINE', 'DESTROYED'];
export const SEGMENTS = ['SPACE', 'GROUND', 'CYBER'];
export const SIGNATURES = ['LARGE', 'NORMAL', 'LOW'];
export const HARDENINGS = ['VULNERABLE', 'NORMAL', 'HARDENED', 'MONITORED'];
export const MOBILITIES = ['STATIC', 'RELOCATABLE', 'MOBILE'];
export const DAMAGE_CLASSES = ['POINT', 'FACILITY'];
export const ASSET_FLAGS = [
    'COVERT',
    'NUCLEAR_ASSOCIATED',
    'DISGUISED',
    'EMITTER',
    'DUAL_USE',
];
/** Task resolution outcomes (§1.4). */
export const OUTCOMES = ['EXCEPTIONAL', 'SUCCESS', 'FAILURE', 'COMPLICATION'];
/** Turn phase machine (§8.1). */
export const GAME_STATUSES = ['LOBBY', 'RUNNING', 'PAUSED', 'COMPLETE', 'ABANDONED'];
export const TURN_PHASES = ['INTEL_REPORT', 'ORDERS', 'RESOLVING'];
/** Turn timing profiles (§2.1): [intel minutes, orders minutes]. */
export const TIMING_PROFILES = ['STANDARD', 'COMPRESSED', 'EXTENDED'];
const MINUTE = 60_000;
export const TIMING_PROFILE_SPECS = Object.freeze({
    STANDARD: { intelMs: 2 * MINUTE, ordersMs: 5 * MINUTE },
    COMPRESSED: { intelMs: 1 * MINUTE, ordersMs: 3 * MINUTE },
    EXTENDED: { intelMs: 3 * MINUTE, ordersMs: 8 * MINUTE },
});
/**
 * Resolution phases (§2.2), as an ordered tuple. The index in this tuple is the
 * `phaseIndex` that seeds every roll (§7.4), so **the order is part of the replay
 * contract** — never reorder, never insert in the middle.
 */
export const RESOLUTION_PHASES = [
    'CYBER',
    'ELECTROMAGNETIC',
    'STATUS',
    'MANEUVER',
    'SENSING',
    'DECAY',
    'KINETIC',
    'ENVIRONMENT',
    'ESCALATION',
    'INTEL',
];
/** 1-based phase number as the rulebook writes it (§2.2). */
export function phaseIndex(phase) {
    return RESOLUTION_PHASES.indexOf(phase) + 1;
}
/**
 * Sensor classes (§5.2.1, §5.8.1).
 *
 * The rulebook's sensing matrices are keyed on the *kind* of sensor, not on the
 * asset type — a scenario may define three different radar sites that all behave
 * as `GROUND_RADAR`. Asset types carry a sensor class; the matrices are indexed
 * by it.
 */
export const SENSOR_CLASSES = [
    'NONE',
    'GROUND_RADAR',
    'GROUND_OPTICAL',
    'SDA_SAT',
    'EO_IMAGING',
    'RADAR_IMAGING',
    'HYPERSPECTRAL',
    'GMTI',
    'PASSIVE_RF',
    'SIGINT_SAT',
];
/** Weather per region (§8.3). Rolled in phase 8 (M3); read by ISR in phase 5. */
export const WEATHER_STATES = ['CLEAR', 'RESTRICTIVE', 'PROHIBITIVE'];
/** Constellation health (§5.3.4.1, §5.7.6). Thresholds are scenario-defined. */
export const CONSTELLATION_HEALTHS = ['NOMINAL', 'DEGRADED', 'OFFLINE'];
/**
 * Sources of a DEGRADED/OFFLINE effect (§5.2). M1 only ever creates
 * `GM_INJECT` and the scenario-seeded ones; the rest arrive with M3, but the
 * vocabulary is fixed now so snapshots written by M1 stay readable.
 */
export const EFFECT_SOURCES = [
    'CYBER_DENY',
    'CYBER_CORRUPT',
    'CYBER_WIPER',
    'JAMMING',
    'JAMMING_EXCEPTIONAL',
    'DAZZLE',
    'GROUND_STRIKE',
    'KKV_ENGAGEMENT',
    'KKV_COMPLICATION',
    'CONJUNCTION',
    'GM_INJECT',
];
/** Where a phantom contact came from (§5.4). */
export const PHANTOM_ORIGINS = [
    'SDA_COMPLICATION',
    'ISR_COMPLICATION',
    'EW_SPOOF',
    'GM_INJECT',
];
/**
 * Every mission in the rulebook, including those no milestone has implemented yet.
 *
 * The whole vocabulary is declared here so the wire enum, the client's mission
 * picker, and the order log share one list. What a given build can actually
 * *resolve* is a different question, answered by the `MISSIONS` table in
 * `data/missions.ts`: a mission id with no table entry is rejected at validation
 * with `MISSION_NOT_IMPLEMENTED` rather than silently doing nothing (§7.3).
 */
export const MISSION_IDS = [
    // SDA (§5.2.2)
    'WIDE_SEARCH',
    'SENSOR_TASK',
    'CUSTODY_WATCH',
    'CHARACTERIZE',
    'DEBRIS_SURVEY',
    // Orbital Warfare (§5.4.2) and orbits (§3.2, §3.3, §3.5)
    'PHASE_SHIFT',
    'TRANSFER',
    'SHADOW',
    'RPO',
    'ENGAGE',
    'BREAK_CUSTODY',
    'DEPLOY',
    'EMISSION_CONTROL',
    'EVADE',
    // Electromagnetic Warfare (§5.5.2)
    'ELECTRONIC_SUPPORT',
    'ELINT',
    'JAMMING',
    'DAZZLE',
    'ELECTRONIC_PROTECTION',
    'SPOOF',
    // Cyber (§5.6.3–5.6.5)
    'RECONNOITER',
    'HUNT',
    'GAIN_ACCESS',
    'ESCALATE_PRIVILEGE',
    'DEPLOY_PAYLOAD',
    'ESTABLISH_PERSISTENCE',
    'HARDEN',
    'MONITOR',
    'ISOLATE',
    'RESTORE',
    // Joint Fires (§5.7)
    'LAUNCH',
    'INTERCEPT',
    // ISR (§5.8.2)
    'AREA_SEARCH',
    'POINT_COLLECTION',
    'DWELL',
    'TARGET_DEVELOPMENT',
    'BATTLE_DAMAGE_ASSESSMENT',
    'PATTERN_OF_LIFE',
    // Ground manoeuvre (§4.4.1) and TT&C (§4.6)
    'RELOCATE',
    'TTC_REASSIGN',
];
/**
 * The custody missions (§1.2): tasking one of these against a contact holds its
 * rung through the target's maneuver. This tuple is the single definition of
 * "custody tasking" — the decay phase reads it, the maneuver phase must not.
 *
 * See §4.11 of docs/milestone_1.md (decision D3): custody exists precisely to
 * keep a track that is trying to shake you, so the maneuver-induced rung drop is
 * evaluated in phase 6 with this list as the guard, not in phase 4.
 */
export const CUSTODY_MISSIONS = ['CUSTODY_WATCH', 'DWELL', 'SHADOW', 'RPO'];
export function isCustodyMission(id) {
    return CUSTODY_MISSIONS.includes(id);
}
// ---------------------------------------------------------------------------
// Cyber (RB §5.6) — M3
// ---------------------------------------------------------------------------
/**
 * The cyber access ladder (RB §5.6.2), in ascending order.
 *
 * `FOOTHOLD` is per **network** and obtained once; `USER` and `ADMIN` are per
 * **asset**. They share one tuple because Hunt "removes one rung" and needs a total
 * order to walk down — see `accessRank`.
 */
export const ACCESS_LEVELS = ['NO_ACCESS', 'FOOTHOLD', 'USER', 'ADMIN'];
export function accessRank(level) {
    return ACCESS_LEVELS.indexOf(level);
}
export function accessAtLeast(level, minimum) {
    return accessRank(level) >= accessRank(minimum);
}
/** One rung down the ladder (RB §5.6.5, Hunt). `NO_ACCESS` is the floor. */
export function demoteAccess(level) {
    const next = ACCESS_LEVELS[Math.max(0, accessRank(level) - 1)];
    return next ?? 'NO_ACCESS';
}
/** Offensive payloads (RB §5.6.4). */
export const CYBER_PAYLOADS = ['COLLECT', 'DENY', 'CORRUPT', 'HIJACK', 'WIPER'];
// ---------------------------------------------------------------------------
// Joint Fires (RB §5.7) — M3
// ---------------------------------------------------------------------------
export const WEAPON_TYPES = ['DA_ASAT', 'SRBM', 'IRBM', 'HGV', 'ICBM', 'INTERCEPTOR'];
/**
 * Missile Warning stages (RB §5.3.4). M3 stores the stage a release happened at,
 * because it sets the Joint Fires interception modifier (RB §5.3.6); M4 supplies
 * the timer that walks a live event through them.
 */
export const MW_STAGES = [1, 2, 3, 4];
export function isMwStage(value) {
    return value === 1 || value === 2 || value === 3 || value === 4;
}
/**
 * What a release is graded *against* (RB §5.3.6): naming the exact model earns the
 * stage bonus, naming a different model of the same category costs `[-2]`, and
 * naming a different category costs `[-4]` and forfeits the bonus.
 *
 * `INTERCEPTOR` is in the list because `MwEvent.trueType` is assigned from a
 * launcher's `weaponType` and the union has to accept one. No default catalogue row
 * uses it and the validator does not require one — an interceptor launch generating
 * a warning event for the other side is a scenario's business, not the engine's.
 */
export const MW_CATEGORIES = [
    ...WEAPON_TYPES,
    'SPACE_LAUNCH',
    'TEST_OR_EXERCISE',
];
/** RB §5.3.5's first column. Small closed vocabulary — it is the stage-1 discriminator. */
export const MW_IR_INTENSITIES = ['LOW', 'MEDIUM', 'HIGH'];
/**
 * Which observable each stage carries (RB §5.3.4), in **canonical order**.
 *
 * The order is part of the replay contract: a DEGRADED constellation drops one
 * field by seeded pick over this list (RB §5.3.4.1), so reordering a stage's fields
 * changes which data point a player loses. Do not reorder.
 *
 * `originSite` and `impactSite` come from the *event* — where this launch started
 * and where its ground track terminates. Everything else comes from the model.
 *
 * **Append within a stage; never reorder one.** `rv` joined stage 4 in M5 (Q10)
 * and went on the end for that reason: `select: 'LAST'` reads the last entry, and
 * putting the new field first would have silently changed which data point a
 * DEGRADED constellation loses. What it does change either way is that stage 4 now
 * has two fields to choose between where it had one — which is a content change,
 * and is part of why `CONTENT_VERSION` moved to 0.5.0.
 */
export const MW_OBSERVABLE_FIELDS = {
    1: ['originSite', 'irIntensity'],
    2: ['boostDurationS', 'stagingEvents'],
    3: ['apogee', 'groundTrack', 'impactSite'],
    4: ['terminal', 'rv'],
};
export const MW_DISPOSITIONS = ['RELEASE', 'DISMISS'];
/** How a DEGRADED constellation loses data (RB §5.3.4.1). Scenario-tunable — M4-D5. */
export const MW_OMISSION_SCOPES = ['PER_STAGE', 'PER_EVENT'];
export const MW_OMISSION_SELECTORS = ['RANDOM', 'LAST'];
// ---------------------------------------------------------------------------
// Escalation (RB §7) — M3
// ---------------------------------------------------------------------------
/** Theatre level bands (RB §7.1). Derived from the counter, never stored. */
export const ESCALATION_LEVELS = ['COMPETITION', 'CRISIS', 'CONFLICT', 'UNCONTROLLED'];
/**
 * How an escalation cause reaches the counter (RB §7.2's third column).
 *
 * `ON_ATTRIBUTION` is the interesting one: the value is banked unapplied and lands
 * *in full and retroactively* when attribution occurs (RB §7.4, §7.7).
 * `OBSERVED` means the victim sees the act itself, so there is nothing to attribute
 * — it applies immediately, exactly like `ALWAYS`, and is a separate name only
 * because the rulebook draws the distinction and a reader will look for it.
 */
export const ATTRIBUTION_MODES = [
    'NOT_APPLICABLE',
    'ON_ATTRIBUTION',
    'ALWAYS',
    'OBSERVED',
];
//# sourceMappingURL=enums.js.map